import { RootState, useAppDispatch } from "@/store";
import { useSelector } from "react-redux";
import { useSubAccount } from "@/hooks";
import { useEffect, useState } from "react";
import {
  getAllSubAccounts$,
  setCurrentSubAccount,
  setDialogs,
  setLoading,
} from "@/store/sub_accounts";
import { Badge, Spinner } from "react-bootstrap";
import { CabinetCard } from "@/components/Card";
import { CustomTable } from "@/components/Table/CustomTable";
import style from "./index.module.scss";
import { useTranslations } from "use-intl";
import { useNavigate } from "react-router-dom";
import { SubAccountSummaryDto } from "@/models";
import { EditWalletAlert } from "@/features/ui/cabinet-alert-components";
import { EDIT_WALLET } from "@/variables";
import { cn } from "@/lib/utils";
import { getHashrateValue } from "@/shared";

const walletAddressText = (wallet: string) =>
  wallet.substring(0, 20) + " " + wallet.substring(20, wallet.length);

export default function SubAccountsPage() {
  const dispatch = useAppDispatch();
  const dialogs = useSelector((state: RootState) => state.subAccount.dialogs);
  const router = useNavigate();
  const [chosenWallet, setChosenWallet] = useState("");
  const [chosenSubAccount, setChosenSubAccount] =
    useState<SubAccountSummaryDto | null>(null);
  const subAccounts = useSelector(
    (state: RootState) => state.subAccount.subAccounts,
  );
  const { currentSubAccount, loading, currentSubAccountInfo } = useSubAccount();
  useEffect(() => {
    let promise: any;
    dispatch(setLoading(true));
    promise = dispatch(getAllSubAccounts$());
    promise.finally(() => dispatch(setLoading(false)));
    return () => {
      dispatch(setLoading(false));
      if (promise) {
        promise.abort();
      }
    };
  }, []);
  const i18n = useTranslations("cabinet");
  const workerInfo = (subAccountInfo: any) => {
    return (
      <div className={style.subaccounts__workerText}>
        {subAccountInfo.summary_data &&
        Object.keys(subAccountInfo.summary_data)?.length ? (
          <>
            <span className={style.green}>
              {subAccountInfo["summary_data"].active_workers}
            </span>{" "}
            /
            <span className={style.red}>
              {subAccountInfo.summary_data.unactive_workers}
            </span>
          </>
        ) : (
          "-"
        )}
      </div>
    );
  };
  return (
    <>
      {currentSubAccountInfo?.summary_data &&
      Object.keys(currentSubAccountInfo.summary_data).length ? (
        <div
          className={cn(
            "flex justify-between gap-4 mb-3",
            style.subaccounts__cardContainer,
          )}
        >
          <CabinetCard className="flex-1">
            <div className={style.card}>
              <span className={style.card__label}>
                {"Total current hashrate"}
              </span>
              <h3 className={style.card__title}>
                {
                  getHashrateValue(
                    currentSubAccountInfo["summary_data"]["hashrate_10min"],
                    currentSubAccountInfo.crypto_currency,
                  ).val
                }
                <span className={style.card__subtitle}>
                  {
                    getHashrateValue(
                      currentSubAccountInfo["summary_data"]["hashrate_10min"],
                      currentSubAccountInfo.crypto_currency,
                    ).unit
                  }
                </span>
              </h3>
            </div>
          </CabinetCard>
          <CabinetCard className="flex-1">
            <div className={style.card}>
              <span className={style.card__label}>
                {"Total average hashrate 24h"}
              </span>
              <h3 className={style.card__title}>
                {
                  getHashrateValue(
                    currentSubAccountInfo["summary_data"]["hashrate_24hour"],
                    currentSubAccountInfo.crypto_currency,
                  ).val
                }
                <span className={style.card__subtitle}>
                  {
                    getHashrateValue(
                      currentSubAccountInfo["summary_data"]["hashrate_24hour"],
                      currentSubAccountInfo.crypto_currency,
                    ).unit
                  }
                </span>
              </h3>
            </div>
          </CabinetCard>
          <CabinetCard className="flex-1">
            <div className={style.card}>
              <span className={style.card__label}>{"Online workers"}</span>
              <h3 className={cn(style.card__title, style.green)}>
                {currentSubAccountInfo["summary_data"]["active_workers"]}
              </h3>
            </div>
          </CabinetCard>
          <CabinetCard className="flex-1">
            <div className={style.card}>
              <span className={style.card__label}>{"Offline workers"}</span>
              <h3 className={cn(style.card__title, style.red)}>
                {currentSubAccountInfo["summary_data"].unactive_workers}
              </h3>
            </div>
          </CabinetCard>
        </div>
      ) : (
        ""
      )}
      {loading ? (
        <Spinner animation={"grow"} />
      ) : (
        <CabinetCard className={"mb-[20px]"}>
          <CustomTable
            notFoundText={i18n("notFoundText")}
            headers={[
              i18n("subaccounts.header.username"),
              i18n("subaccounts.header.worker"),
              i18n("subaccounts.header.current_hashrate"),
              i18n("subaccounts.header.average_hashrate"),
              i18n("subaccounts.header.wallet"),
              i18n("subaccounts.header.currency"),
              i18n("subaccounts.header.earningScheme"),
            ]}
            columns={subAccounts.map((account) => {
              return {
                data: [
                  "name",
                  "worker",
                  "current_hashrate",
                  "average_hashrate",
                  "wallet_address",
                  "crypto_currency",
                  "earning_scheme",
                ].map((key) => {
                  if (key === "name") {
                    return (
                      <div>
                        <div>{account.name}</div>
                        {account.is_pending_deletion && (
                          <div>
                            <Badge bg="warning" text="dark" style={{ fontSize: "11px" }}>
                              ⚠ {i18n("subaccounts.pendingDeletion")}
                            </Badge>
                            {account.pending_deletion_message && (
                              <div className="text-muted small mt-1">
                                {account.pending_deletion_message}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  if (key === "wallet_address") {
                    return (
                      <div className={style.subaccounts__wallet}>
                        <div className={style.subaccounts__wallet__text}>
                          {walletAddressText(account[key])}
                        </div>
                        <div
                          className={style.subaccounts__wallet__img}
                          onClick={() => {
                            setChosenWallet(account.wallet_address);
                            if (account.virtual_sub_accounts?.length > 1) {
                              dispatch(setCurrentSubAccount(account.name));
                              router("/cabinet/settings?page=personal");
                            } else {
                              setChosenSubAccount(account);
                              dispatch(
                                setDialogs({
                                  key: EDIT_WALLET,
                                  value: true,
                                }),
                              );
                            }
                          }}
                        >
                          <img
                            src={require("@/images/main/edit.svg").default}
                            alt={"edit"}
                          />
                        </div>
                      </div>
                    );
                  }
                  if (key === "worker") {
                    return workerInfo(account);
                  }
                  if (key === "current_hashrate") {
                    return (
                      <span>
                        {account.summary_data?.hashrate_10min
                          ? getHashrateValue(
                              account.summary_data?.hashrate_10min,
                              account.crypto_currency,
                            ).val
                          : "-"}
                      </span>
                    );
                  }
                  if (key === "average_hashrate") {
                    return (
                      <span>
                        {account.summary_data?.hashrate_24hour
                          ? getHashrateValue(
                              account.summary_data?.hashrate_24hour,
                              account.crypto_currency,
                            ).val
                          : "-"}
                      </span>
                    );
                  }
                  return account[key];
                }),
              };
            })}
          />
        </CabinetCard>
      )}
      {dialogs[EDIT_WALLET] && chosenSubAccount && (
        <EditWalletAlert
          currentWallet={chosenWallet}
          subAccount={chosenSubAccount}
        />
      )}
    </>
  );
}
